import { FC, useState, useCallback, useMemo, useEffect } from "react";
import { Box, Stack, Typography, styled, Modal as MuiModal } from "@mui/material";
import { background, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { IconSave } from "@/assets/icons";
import { SAVED_CONDITION_TITLE_MAX_LENGTH, TITLE_MAX_LENGTH_MESSAGE, TITLE_REQUIRED_MESSAGE } from "@/constants/common";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import {
  TCaseEditMatchFilterFormExtended,
  defaultCaseEditMatchFilterValues,
  normalizeCaseEditMatchMultiSelectFields,
} from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { CaseEditMatchFilterFormFields } from "@/features/case/components/CaseEdit/CaseEditMatchFilterFormFields";
import { TOption } from "@/types/common";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string, filters: TCaseEditMatchFilterFormExtended) => void | Promise<void>;
  initialTitle: string;
  initialFilters: TCaseEditMatchFilterFormExtended;
  interviewDateOptions?: TOption[];
};

const serialize = (title: string, filters: TCaseEditMatchFilterFormExtended): string => JSON.stringify({ title, filters });

export const CaseEditMatchEditSavedConditionModal: FC<Props> = ({ isOpen, onClose, onSave, initialTitle, initialFilters, interviewDateOptions }) => {
  const [title, setTitle] = useState(initialTitle);
  const [titleError, setTitleError] = useState("");
  const [filterValues, setFilterValues] = useState<TCaseEditMatchFilterFormExtended>({
    ...defaultCaseEditMatchFilterValues,
    ...normalizeCaseEditMatchMultiSelectFields(initialFilters as Partial<Record<string, unknown>>),
  });
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState("");

  useEffect(() => {
    if (isOpen) {
      const normalized = {
        ...defaultCaseEditMatchFilterValues,
        ...normalizeCaseEditMatchMultiSelectFields(initialFilters as Partial<Record<string, unknown>>),
      };
      setTitle(initialTitle);
      setTitleError("");
      setFilterValues(normalized);
      setInitialSnapshot(serialize(initialTitle, normalized));
    }
  }, [isOpen, initialTitle, initialFilters]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return serialize(title, filterValues) !== initialSnapshot;
  }, [title, filterValues, initialSnapshot]);

  const handleFilterChange = useCallback((field: keyof TCaseEditMatchFilterFormExtended, value: unknown) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError(TITLE_REQUIRED_MESSAGE);
      return;
    }
    if (trimmed.length > SAVED_CONDITION_TITLE_MAX_LENGTH) {
      setTitleError(TITLE_MAX_LENGTH_MESSAGE);
      return;
    }
    await onSave(trimmed, filterValues);
    onClose();
  }, [title, filterValues, onSave, onClose]);

  const handleAttemptClose = useCallback(() => {
    if (isDirty) {
      setIsConfirmLeaveOpen(true);
    } else {
      setTitleError("");
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirmLeave = useCallback(() => {
    setIsConfirmLeaveOpen(false);
    setTitleError("");
    onClose();
  }, [onClose]);

  return (
    <MuiModal
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleAttemptClose();
      }}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <ModalContainer>
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Typography variant="cap12Light">
            タイトル{" "}
            <Typography component="span" sx={{ color: text.error }}>
              *
            </Typography>
          </Typography>
          <Input
            placeholder="入力してください"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError("");
            }}
            size="md"
            nonControl
            error={Boolean(titleError)}
          />
          <Typography variant="cap12Light" sx={{ color: titleError ? text.error : text.tertiary }}>
            {titleError || TITLE_MAX_LENGTH_MESSAGE}
          </Typography>
        </Stack>

        <CaseEditMatchFilterFormFields filterValues={filterValues} onFilterChange={handleFilterChange} interviewDateOptions={interviewDateOptions} />

        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 3 }}>
          <Button type="button" variant="ghost" onClick={handleAttemptClose} size="sm">
            キャンセル
          </Button>
          <Button type="button" onClick={handleSave} size="sm" startIcon={<IconSave />}>
            保存
          </Button>
        </Stack>

        <ConfirmDeleteModal
          open={isConfirmLeaveOpen}
          onClose={() => setIsConfirmLeaveOpen(false)}
          onOk={handleConfirmLeave}
          message="変更内容が保存されていません。このまま閉じますか？"
          title="確認"
        />
      </ModalContainer>
    </MuiModal>
  );
};

const ModalContainer = styled(Box)`
  width: 720px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 32px;
  outline: none;
  &:focus {
    outline: none;
  }
`;
